import React, { useEffect, useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { getWorkspaces } from '../../api/client';

interface Workspace {
  id: string;
  name: string;
}

interface WorkspaceSelectorProps {
  accountId: string | null;
  onSelect: (workspaceId: string, workspaceName: string, source: 'resolve' | 'user') => void;
  selectedWorkspaceId: string | null;
  requestedWorkspaceId?: string | null;
}

const WorkspaceSelector: React.FC<WorkspaceSelectorProps> = ({
  accountId,
  onSelect,
  selectedWorkspaceId,
  requestedWorkspaceId,
}) => {
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [loadedAccountId, setLoadedAccountId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState('');

  useEffect(() => {
    if (!accountId) {
      setWorkspaces([]);
      setLoadedAccountId(null);
      setSearchValue('');
      return;
    }

    let cancelled = false;
    const fetchWorkspaces = async () => {
      setLoading(true);
      setError(null);
      setWorkspaces([]);
      setLoadedAccountId(null);
      setSearchValue('');
      try {
        const data: Workspace[] = await getWorkspaces(accountId);
        if (!cancelled) {
          setWorkspaces(data);
          setLoadedAccountId(accountId);
        }
      } catch (err) {
        if (!cancelled) setError('Failed to load workspaces');
        console.error(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchWorkspaces();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId]);

  useEffect(() => {
    if (workspaces.length === 0 || loadedAccountId !== accountId) return;
    const selectedWorkspace = workspaces.find(workspace => String(workspace.id) === selectedWorkspaceId);
    const requestedWorkspace = workspaces.find(workspace => String(workspace.id) === requestedWorkspaceId);
    const resolvedWorkspace = requestedWorkspace || (!requestedWorkspaceId ? selectedWorkspace : null) || workspaces[0];
    if (resolvedWorkspace && String(resolvedWorkspace.id) !== selectedWorkspaceId) {
      onSelect(String(resolvedWorkspace.id), resolvedWorkspace.name, 'resolve');
    }
    // onSelect is intentionally excluded because ChatContainer recreates it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, loadedAccountId, workspaces, requestedWorkspaceId, selectedWorkspaceId]);

  const filteredWorkspaces = useMemo(() => {
    const search = searchValue.trim().toLowerCase();
    if (!search) return workspaces;
    return workspaces.filter(workspace => workspace.name.toLowerCase().includes(search));
  }, [searchValue, workspaces]);

  if (!accountId) return <div className="context-nav-empty">Select an account</div>;
  if (loading) return <div className="context-nav-status">Loading workspaces...</div>;
  if (error) return <div className="context-nav-status context-nav-error">{error}</div>;

  return (
    <div className="workspace-navigation">
      <span className="context-nav-label">Workspaces</span>
      <label className="context-search">
        <Search size={15} />
        <input
          aria-label="Search workspaces"
          placeholder="Search workspaces"
          value={searchValue}
          onChange={event => setSearchValue(event.target.value)}
        />
      </label>
      <div className="workspace-list" aria-label="Workspaces">
        {filteredWorkspaces.map(workspace => {
          const workspaceId = String(workspace.id);
          return (
            <button
              type="button"
              className={`workspace-list-item${workspaceId === selectedWorkspaceId ? ' active' : ''}`}
              aria-current={workspaceId === selectedWorkspaceId ? 'page' : undefined}
              key={workspace.id}
              title={workspace.name}
              onClick={() => {
                if (workspaceId !== selectedWorkspaceId) onSelect(workspaceId, workspace.name, 'user');
              }}
            >
              {workspace.name}
            </button>
          );
        })}
        {filteredWorkspaces.length === 0 && (
          <div className="context-nav-empty">
            {workspaces.length === 0 ? 'No workspaces' : 'No workspaces found'}
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkspaceSelector;
